import React, { useState, useRef, useEffect } from 'react';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, startAfter, limit, Timestamp, QueryConstraint, doc, getDoc, documentId } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import * as XLSX from 'xlsx';

// redux
import { useAppSelector } from '../../../redux/store';
import getCurrentScreen from '../../../Helper/GetScreen';
import Toast from '../../../components/Toast';

interface Student {
    id: string;
    name: string;
    createdAt: Timestamp;
    phone: string;
    source: { source: string };
    stage: { color: string; priority: number; stage: string };
    priority: { color: string; priority: number; status: string };
    admissionStatus?: {
        admissionStatus: string;
    };
    assignedTo?: {
        name: string;
    };
    collaborators?: string[];
    collaborateDate?: Timestamp;
    followUp?: {
        followUp: string;
        followUpTime: Timestamp;
        userId: string;
        isDone: boolean;
        leadId: string;
    };
}

interface UserData {
    companyId: string;
    isAdmin: boolean;
    name: string;
}

interface FilterOption {
    id: string;
    name?: string;
    source?: string;
    stage?: string;
    status?: string;
    color?: string;
    admissionStatus?: string;
}

type SortOrder = 'asc' | 'desc' | null;

const Collaborations = () => {

    const { currentUser, isAdmin, isManager, isStaff, userData } = useAuth();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const [colabCatogory, setColabCategory] = useState<'Collaborated with me' | 'Assigned to me'>('Collaborated with me');

    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);

    const [students, setStudents] = useState<Student[]>([]);
    const [companyId, setCompanyId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc'); // Default to descending order
    const [filters, setFilters] = useState({
        source: [] as string[],
        stage: [] as string[],
        priority: [] as string[],
        admissionStatus: [] as string[],
        startDate: '',
        endDate: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        sources: [] as FilterOption[],
        stages: [] as FilterOption[],
        priorities: [] as FilterOption[],
        admissionStatuses: [] as FilterOption[]
    });
    const filterPopupRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const PAGE_SIZE = 10;

    // total students count in collaborations
    const [totalLeads, setTotalLeads] = useState(0);

    // Sort students by collaborate date
    const sortStudentsByDate = (studentsToSort: Student[], order: SortOrder) => {
        if (!order) return studentsToSort;

        return [...studentsToSort].sort((a, b) => {
            // Handle cases where collaborateDate might be undefined
            const dateA = a.collaborateDate ? a.collaborateDate.toDate().getTime() : 0;
            const dateB = b.collaborateDate ? b.collaborateDate.toDate().getTime() : 0;

            if (order === 'asc') {
                return dateA - dateB;
            } else {
                return dateB - dateA;
            }
        });
    };

    // Handle sort toggle
    const handleSortToggle = () => {
        let newSortOrder: SortOrder;
        if (sortOrder === 'desc') {
            newSortOrder = 'asc'; // Switch to ascending
        } else if (sortOrder === 'asc') {
            newSortOrder = null; // Switch to no sorting
        } else {
            newSortOrder = 'desc'; // Start with descending
        }

        setSortOrder(newSortOrder);

        // Sort current students
        const sortedStudents = sortStudentsByDate(students, newSortOrder);
        setStudents(sortedStudents);
    };

    // Fetch filter options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [sourcesSnap, stagesSnap, prioritiesSnap, admissionStatusesSnap] = await Promise.all([
                    getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`))
                ]);

                setFilterOptions({
                    sources: sourcesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    stages: stagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    priorities: prioritiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    admissionStatuses: admissionStatusesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                });
            } catch (error) {
                console.error('Error fetching filter options:', error);
            }
        };

        fetchFilterOptions();
    }, []);

    // Get user data including company ID
    useEffect(() => {
        const getUserData = async () => {
            if (!currentUser) return;
            try {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const data = userDocSnap.data() as UserData;
                    setCompanyId(data.companyId);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };
        getUserData();
    }, [currentUser]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch collaborated students
    const fetchCollaboratedStudents = async (isNewQuery: boolean = true) => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            const studentsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Base query conditions - REMOVED limit(PAGE_SIZE) and pagination logic
            const conditions: QueryConstraint[] = [
                where('isActive', '==', true),
                where('collaborators', 'array-contains', currentUser.uid),
                orderBy('createdAt', 'desc'),
                orderBy('name', 'asc')
                // Removed: limit(PAGE_SIZE)
            ];

            // Add search condition if search query exists
            if (searchQuery.trim()) {
                const upperCaseQuery = searchQuery.trim().toUpperCase();
                conditions.push(where('name', '>=', upperCaseQuery));
                conditions.push(where('name', '<=', upperCaseQuery + '\uf8ff'));
            }

            // Add filters if selected
            if (filters.source.length > 0) {
                conditions.push(where('sourceId', 'in', filters.source));
            }
            if (filters.stage.length > 0) {
                conditions.push(where('stageId', 'in', filters.stage));
            }
            if (filters.priority.length > 0) {
                conditions.push(where('statusId', 'in', filters.priority));
            }
            if (filters.admissionStatus.length > 0) {
                conditions.push(where('admissionStatusId', 'in', filters.admissionStatus));
            }

            // Add date range filters if selected
            if (filters.startDate) {
                const startDate = new Date(filters.startDate);
                startDate.setHours(0, 0, 0, 0);
                conditions.push(where('collaborateDate', '>=', Timestamp.fromDate(startDate)));
            }
            if (filters.endDate) {
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                conditions.push(where('collaborateDate', '<=', Timestamp.fromDate(endDate)));
            }

            // Removed pagination logic - no startAfter condition

            const studentsQuery = query(studentsRef, ...conditions);
            const snapshot = await getDocs(studentsQuery);

            // No pagination state updates needed
            // setLastVisible and setHasMore removed

            const studentsData = await Promise.all(snapshot.docs.map(async (doc) => {
                const data = doc.data();
                const sourceDoc = data.sourceId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)).then(
                    snap => snap.docs.find(d => d.id === data.sourceId)?.data()
                ) : null;
                const stageDoc = data.stageId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)).then(
                    snap => snap.docs.find(d => d.id === data.stageId)?.data()
                ) : null;
                const priorityDoc = data.statusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)).then(
                    snap => snap.docs.find(d => d.id === data.statusId)?.data()
                ) : null;
                const admissionStatusDoc = data.admissionStatusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`)).then(
                    snap => snap.docs.find(d => d.id === data.admissionStatusId)?.data()
                ) : null;

                // Fetch assigned user information
                const assignedToDoc = data.assignedTo ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.assignedTo)?.data()
                ) : null;

                // Fetch collaborators from the users collection where companyId matches using query
                const collaboratorsQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where(documentId(), 'in', data.collaborators || [])
                );
                const collaboratorsSnapshot = await getDocs(collaboratorsQuery);
                const collaboratorsDoc = collaboratorsSnapshot.docs.map(collab => ({
                    id: collab.id,
                    name: collab.data().name || 'Unknown',
                    ...collab.data()
                }));

                // Fetch total leads count for collaborated students
                const snapshot = await getDocs(studentsQuery);
                setTotalLeads(snapshot.size);

                // Fetch follow-up for this student
                const followUpQuery = query(
                    collection(db, `companies/${userData.companyId}/followUps`),
                    where('leadId', '==', doc.id),
                    where('isDone', '==', false),
                    limit(1)
                );
                const followUpSnapshot = await getDocs(followUpQuery);
                const followUp = followUpSnapshot.docs[0]?.data();
                return {
                    id: doc.id,
                    name: data.name,
                    createdAt: data.createdAt,
                    phone: data.number || '',
                    source: {
                        source: sourceDoc?.source || 'Not Specified'
                    },
                    stage: {
                        stage: stageDoc?.stage || 'N/A',
                        color: stageDoc?.color || '#808080',
                        priority: stageDoc?.priority || 0
                    },
                    priority: {
                        status: priorityDoc?.status || 'N/A',
                        color: priorityDoc?.color || '#808080',
                        priority: priorityDoc?.priority || 0
                    },
                    admissionStatus: admissionStatusDoc ? {
                        admissionStatus: admissionStatusDoc.admissionStatus
                    } : undefined,
                    assignedTo: assignedToDoc ? {
                        name: assignedToDoc.name || 'Unknown'
                    } : undefined,
                    collaborators: collaboratorsDoc.map(collab => collab.name),
                    collaborateDate: data.collaborateDate,
                    followUp: followUp || undefined
                } as Student;
            }));

            // Apply sorting to all results and set students
            const finalStudents = sortStudentsByDate(studentsData, sortOrder);
            setStudents(finalStudents);

            // Set hasMore to false since we loaded all data
            setHasMore(false);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch my assigned students which has collaborators array not empty
    const fetchMyAssignedStudents = async (isNewQuery: boolean = true) => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            const studentsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Base query conditions - REMOVED limit(PAGE_SIZE) and pagination logic
            const conditions: QueryConstraint[] = [
                where('isActive', '==', true),
                where('assignedTo', '==', currentUser.uid),
                where('collaborators', '!=', []), // Ensure collaborators array is not empty
                orderBy('createdAt', 'desc'),
                orderBy('name', 'asc')
                // Removed: limit(PAGE_SIZE)
            ];

            // Add search condition if search query exists
            if (searchQuery.trim()) {
                const upperCaseQuery = searchQuery.trim().toUpperCase();
                conditions.push(where('name', '>=', upperCaseQuery));
                conditions.push(where('name', '<=', upperCaseQuery + '\uf8ff'));
            }

            // Add filters if selected
            if (filters.source.length > 0) {
                conditions.push(where('sourceId', 'in', filters.source));
            }
            if (filters.stage.length > 0) {
                conditions.push(where('stageId', 'in', filters.stage));
            }
            if (filters.priority.length > 0) {
                conditions.push(where('statusId', 'in', filters.priority));
            }
            if (filters.admissionStatus.length > 0) {
                conditions.push(where('admissionStatusId', 'in', filters.admissionStatus));
            }

            // Add date range filters if selected
            if (filters.startDate) {
                const startDate = new Date(filters.startDate);
                startDate.setHours(0, 0, 0, 0);
                conditions.push(where('collaborateDate', '>=', Timestamp.fromDate(startDate)));
            }
            if (filters.endDate) {
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                conditions.push(where('collaborateDate', '<=', Timestamp.fromDate(endDate)));
            }

            // Removed pagination logic - no startAfter condition

            const studentsQuery = query(studentsRef, ...conditions);
            const snapshot = await getDocs(studentsQuery);

            // No pagination state updates needed
            // setLastVisible and setHasMore removed

            const studentsData = await Promise.all(snapshot.docs.map(async (doc) => {
                const data = doc.data();
                const sourceDoc = data.sourceId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)).then(
                    snap => snap.docs.find(d => d.id === data.sourceId)?.data()
                ) : null;
                const stageDoc = data.stageId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)).then(
                    snap => snap.docs.find(d => d.id === data.stageId)?.data()
                ) : null;
                const priorityDoc = data.statusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)).then(
                    snap => snap.docs.find(d => d.id === data.statusId)?.data()
                ) : null;
                const admissionStatusDoc = data.admissionStatusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`)).then(
                    snap => snap.docs.find(d => d.id === data.admissionStatusId)?.data()
                ) : null;

                // Fetch assigned user information
                const assignedToDoc = data.assignedTo ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.assignedTo)?.data()
                ) : null;

                // Fetch collaborators from the users collection where companyId matches using query
                const collaboratorsQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where(documentId(), 'in', data.collaborators || [])
                );
                const collaboratorsSnapshot = await getDocs(collaboratorsQuery);
                const collaboratorsDoc = collaboratorsSnapshot.docs.map(collab => ({
                    id: collab.id,
                    name: collab.data().name || 'Unknown',
                    ...collab.data()
                }));

                // Fetch total leads count for collaborated students
                const snapshot = await getDocs(studentsQuery);
                setTotalLeads(snapshot.size);

                // Fetch follow-up for this student
                const followUpQuery = query(
                    collection(db, `companies/${userData.companyId}/followUps`),
                    where('leadId', '==', doc.id),
                    where('isDone', '==', false),
                    limit(1)
                );
                const followUpSnapshot = await getDocs(followUpQuery);
                const followUp = followUpSnapshot.docs[0]?.data();
                return {
                    id: doc.id,
                    name: data.name,
                    createdAt: data.createdAt,
                    phone: data.number || '',
                    source: {
                        source: sourceDoc?.source || 'Not Specified'
                    },
                    stage: {
                        stage: stageDoc?.stage || 'N/A',
                        color: stageDoc?.color || '#808080',
                        priority: stageDoc?.priority || 0
                    },
                    priority: {
                        status: priorityDoc?.status || 'N/A',
                        color: priorityDoc?.color || '#808080',
                        priority: priorityDoc?.priority || 0
                    },
                    admissionStatus: admissionStatusDoc ? {
                        admissionStatus: admissionStatusDoc.admissionStatus
                    } : undefined,
                    assignedTo: assignedToDoc ? {
                        name: assignedToDoc.name || 'Unknown'
                    } : undefined,
                    collaborators: collaboratorsDoc.map(collab => collab.name),
                    collaborateDate: data.collaborateDate,
                    followUp: followUp || undefined
                } as Student;
            }));

            // Apply sorting to all results and set students
            const finalStudents = sortStudentsByDate(studentsData, sortOrder);
            setStudents(finalStudents);

            // Set hasMore to false since we loaded all data
            setHasMore(false);
        } catch (error) {
            console.error('Error fetching my assigned students:', error);
        }
        finally {
            setLoading(false);
        }
    }

    // Initial fetch when user data is loaded
    useEffect(() => {
        if (userData) {
            if (colabCatogory === 'Collaborated with me') {
                fetchCollaboratedStudents();
            } else if (colabCatogory === 'Assigned to me') {
                fetchMyAssignedStudents();
            }
        }
    }, [userData, colabCatogory]);

    // excel download
    const exportStudentsToExcel = async () => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            const studentsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Base query conditions - removing pagination limit for full data
            let conditions: QueryConstraint[] = [
                where('isActive', '==', true),
                orderBy('createdAt', 'desc'),
                orderBy('name', 'asc')
            ];

            // Add search condition if search query exists
            if (searchQuery.trim()) {
                const upperCaseQuery = searchQuery.trim().toUpperCase();
                conditions.push(where('name', '>=', upperCaseQuery));
                conditions.push(where('name', '<=', upperCaseQuery + '\uf8ff'));
            }

            // Add filters if selected and non-empty
            if (filters.source && filters.source.length > 0) {
                conditions.push(where('sourceId', 'in', filters.source));
            }
            if (filters.stage && filters.stage.length > 0) {
                conditions.push(where('stageId', 'in', filters.stage));
            }
            if (filters.priority && filters.priority.length > 0) {
                conditions.push(where('statusId', 'in', filters.priority));
            }
            if (filters.admissionStatus && filters.admissionStatus.length > 0) {
                conditions.push(where('admissionStatusId', 'in', filters.admissionStatus));
            }

            // Add date range filters if selected
            if (filters.startDate) {
                const startDate = new Date(filters.startDate);
                startDate.setHours(0, 0, 0, 0);
                conditions.push(where('collaborateDate', '>=', Timestamp.fromDate(startDate)));
            }
            if (filters.endDate) {
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                conditions.push(where('collaborateDate', '<=', Timestamp.fromDate(endDate)));
            }

            // Add user-specific condition for non-admin users
            if (isStaff && currentScreen === "Staff") {
                conditions.push(where('collaborators', 'array-contains', currentUser.uid));
            }

            const studentsQuery = query(studentsRef, ...conditions);
            const snapshot = await getDocs(studentsQuery);

            const studentsData = await Promise.all(snapshot.docs.map(async (doc) => {
                const data = doc.data();

                // Fetch source, stage, status, and state data
                const sourceDoc = data.sourceId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)).then(
                    snap => snap.docs.find(d => d.id === data.sourceId)?.data()
                ) : null;
                const stageDoc = data.stageId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)).then(
                    snap => snap.docs.find(d => d.id === data.stageId)?.data()
                ) : null;
                const priorityDoc = data.statusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)).then(
                    snap => snap.docs.find(d => d.id === data.statusId)?.data()
                ) : null;
                const stateDoc = data.stateId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStates`)).then(
                    snap => snap.docs.find(d => d.id === data.stateId)?.data()
                ) : null;
                const admissionStatusDoc = data.admissionStatusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`)).then(
                    snap => snap.docs.find(d => d.id === data.admissionStatusId)?.data()
                ) : null;

                // Fetch assigned user and updated by user emails with company filter
                const assignedToDoc = data.assignedTo ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.assignedTo)?.data()
                ) : null;

                const updatedByDoc = data.updatedBy ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.updatedBy)?.data()
                ) : null;

                // Fetch follow-up for this student
                const followUpQuery = query(
                    collection(db, `companies/${userData.companyId}/followUps`),
                    where('leadId', '==', doc.id),
                    where('isDone', '==', false),
                    orderBy('dueDate', 'asc'),
                    limit(1)
                );
                const followUpSnapshot = await getDocs(followUpQuery);
                const followUp = followUpSnapshot.docs[0]?.data();

                return {
                    name: data.name || '',
                    email: data.email || '',
                    number: data.number || '',
                    createdAt: data.createdAt?.toDate().toLocaleDateString() || '',
                    updatedAt: data.updatedAt?.toDate().toLocaleDateString() || '',
                    adminNotes: data.adminNotes || '',
                    destinationCountry: data.destinationCountry || '',
                    destinationCountryCode: data.destinationCountryCode || '',
                    intakeMonth: data.intakeMonth || '',
                    intakeYear: data.intakeYear || '',
                    // isActive: data.isActive?.toString() || '',
                    preferredStream: data.preferredStream || '',
                    programType: data.programType || '',
                    stage: stageDoc?.stage || '',
                    state: stateDoc?.state || '',
                    status: priorityDoc?.status || '',
                    assignedTo: assignedToDoc?.email || '',
                    updatedBy: updatedByDoc?.email || '',
                    Source: sourceDoc?.source || '-',
                    AdmissionStatus: admissionStatusDoc?.admissionStatus || '-',
                    Followup: followUp?.notes || '',
                    'Followup Date': followUp?.dueDate?.toDate().toLocaleDateString() || ''
                };
            }));

            console.log('Filtered Students Data:', studentsData);
            console.log('Total Records:', studentsData.length);

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(studentsData);

            // Create workbook and add worksheet
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Students');

            // Generate Excel file
            const fileName = `students_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (error) {
            console.error('Error exporting students:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle search
    const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setStudents([]);
            setLastVisible(null);
            setHasMore(true);
            if (colabCatogory === 'Collaborated with me') {
                fetchCollaboratedStudents();
            } else if (colabCatogory === 'Assigned to me') {
                fetchMyAssignedStudents();
            }
        }
    };

    // Handle filter changes
    const handleFilterChange = (type: 'source' | 'stage' | 'priority' | 'admissionStatus', value: string) => {
        setFilters(prev => ({
            ...prev,
            [type]: prev[type].includes(value)
                ? prev[type].filter(item => item !== value)
                : [...prev[type], value]
        }));
    };

    // Apply filters
    const applyFilters = () => {
        setStudents([]);
        setLastVisible(null);
        setHasMore(true);
        if (colabCatogory === 'Collaborated with me') {
            fetchCollaboratedStudents();
        } else if (colabCatogory === 'Assigned to me') {
            fetchMyAssignedStudents();
        }
        setIsFilterOpen(false);
    };

    // clear fillter
    const clearFilters = () => {
        setFilters({
            source: [],
            stage: [],
            priority: [],
            admissionStatus: [],
            startDate: '',
            endDate: ''
        });
    };


    useEffect(() => {
        if (colabCatogory === 'Collaborated with me') {
            fetchCollaboratedStudents();
        } else if (colabCatogory === 'Assigned to me') {
            fetchMyAssignedStudents();
        }
    }, [filters]);

    // Load more
    const loadMore = () => {
        if (hasMore && !loading) {
            if (colabCatogory === 'Collaborated with me') {
                fetchCollaboratedStudents(false);
            } else if (colabCatogory === 'Assigned to me') {
                fetchMyAssignedStudents(false);
            }
        }
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

    // cpoy the lead's phone number to clipboard
    const copyPhoneNumberToClipboard = (phoneNumber: string) => {
        navigator.clipboard.writeText(phoneNumber)
            .then(() => {
                setToastMessage('Phone number copied to clipboard!'); setIsErrorToast(false); setShowToast(true);
            })
            .catch((error) => {
                setToastMessage('Failed to copy phone number'); setIsErrorToast(true); setShowToast(true);
                console.error('Error copying phone number:', error);
            });
    };

    // Get sort icon based on current sort order
    const getSortIcon = () => {
        if (sortOrder === 'desc') {
            return '↓'; // Down arrow for descending (newest first)
        } else if (sortOrder === 'asc') {
            return '↑'; // Up arrow for ascending (oldest first)
        } else {
            return '↕'; // Up-down arrow for no sorting
        }
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-10">
                <TopNavbar />
            </div> */}

            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md">
                    <div className="flex items-center gap-6 mb-8 relative">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/book-search-24.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-black">My Collaborations</h2>
                        <div className="ml-5 flex items-center w-80 rounded-3xl bg-zinc-100">
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                alt="search-lens-icon"
                                className="w-7 h-7 ml-2 cursor-pointer"
                                onClick={() => {
                                    setStudents([]);
                                    setLastVisible(null);
                                    setHasMore(true);
                                    if (colabCatogory === 'Collaborated with me') {
                                        fetchCollaboratedStudents();
                                    } else if (colabCatogory === 'Assigned to me') {
                                        fetchMyAssignedStudents();
                                    }
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search for Student..."
                                className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleSearch}
                            />
                        </div>

                        <div className="ml-auto relative">
                            <div className="ml-auto flex items-center gap-4">

                                {/* {isAdmin ? (
                                <button className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2 opacity-50 "
                                    style={{ background: "linear-gradient(180deg, #0313fc 0%, #2003fc 100%)" }}
                                    onClick={() => { exportStudentsToExcel() }}
                                >
                                    Download Data
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/material-download.svg`} alt="Download Icon" className="w-4 h-4" />
                                </button>
                                ) : null} */}

                                <div className="mb-4 px-2 mt-5">
                                    <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                                        <span className="text-sm font-medium text-blue-800">
                                            Total Leads: <span className="font-bold">{totalLeads}</span>
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                                    className="text-gray-600 hover:text-gray-800"
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                        alt="filters-icon"
                                        className="w-[35px] h-[35px]"
                                    />
                                </button>
                            </div>

                            {isFilterOpen && (
                                <div
                                    ref={filterPopupRef}
                                    className="absolute top-0 right-0 transform translate-x-3 w-[261px] h-[330px] bg-white border rounded-3xl shadow-lg z-50 flex flex-col"
                                >
                                    <div className="flex justify-between items-center p-4">
                                        <h3 className="text-[14px] font-normal leading-[20px] text-left">Filters</h3>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                            alt="filters-black-icon"
                                            onClick={() => setIsFilterOpen(false)}
                                            className="w-9 h-9"
                                        />
                                    </div>

                                    <div className="flex-1 overflow-y-auto px-4" style={{ maxHeight: "calc(100% - 120px)" }}>
                                        <div className="space-y-4">
                                            {/* Date Range Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Date Range:</span>
                                                <div className="mt-2 space-y-2">
                                                    <div>
                                                        <label className="text-[10px] text-gray-600">Start Date:</label>
                                                        <input
                                                            type="date"
                                                            value={filters.startDate}
                                                            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                                                            className="w-full px-2 py-1 text-[12px] border border-gray-300 rounded focus:outline-none focus:border-orange-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-gray-600">End Date:</label>
                                                        <input
                                                            type="date"
                                                            value={filters.endDate}
                                                            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                                                            className="w-full px-2 py-1 text-[12px] border border-gray-300 rounded focus:outline-none focus:border-orange-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Source Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Source:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.sources.map(source => (
                                                        <label key={source.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.source.includes(source.id)}
                                                                    onChange={() => handleFilterChange('source', source.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{source.source}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Stage Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Stage:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.stages.map(stage => (
                                                        <label key={stage.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.stage.includes(stage.id)}
                                                                    onChange={() => handleFilterChange('stage', stage.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{stage.stage}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Priority Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Priority:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.priorities.map(priority => (
                                                        <label key={priority.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.priority.includes(priority.id)}
                                                                    onChange={() => handleFilterChange('priority', priority.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{priority.status}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Admission Status Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Admission Status:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.admissionStatuses.map(status => (
                                                        <label key={status.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.admissionStatus.includes(status.id)}
                                                                    onChange={() => handleFilterChange('admissionStatus', status.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{status.admissionStatus}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4 border-t">
                                        <div className="flex justify-between space-x-3">
                                            <button
                                                onClick={clearFilters}
                                                className="px-8 py-2 text-white bg-black rounded-full"
                                            >
                                                Clear
                                            </button>
                                            <button
                                                onClick={applyFilters}
                                                className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* Category buttons */}
                    <div className="flex items-center justify-evenly gap-1 mb-6 w-full border border-gray-100 rounded-lg p-0.5">
                        <button
                            className={`py-1 w-full bg-gray-100 text-black text-sm rounded-lg hover:bg-gray-200 ${colabCatogory === 'Collaborated with me' ? 'bg-gray-300' : ''}`}
                            onClick={() => setColabCategory('Collaborated with me')}>
                            Collaborated with me
                        </button>
                        <button
                            className={`py-1 w-full bg-gray-100 text-black text-sm rounded-lg hover:bg-gray-200 ${colabCatogory === 'Assigned to me' ? 'bg-gray-300' : ''}`}
                            onClick={() => setColabCategory('Assigned to me')}>
                            Assigned to me
                        </button>
                    </div>

                    {/* Students Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collaborators</th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 select-none"
                                        onClick={handleSortToggle}
                                        title="Click to sort by collaborate date"
                                    >
                                        <div className="flex items-center gap-1">
                                            Collaborate Date
                                            <span className="text-gray-400 text-lg">{getSortIcon()}</span>
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {students.map((student) => (
                                    <tr key={student.id} className="hover:bg-gray-50">
                                        <td
                                            className="px-6 py-4 whitespace-nowrap cursor-pointer"
                                            onClick={() => handleStudentClick(student.id)}
                                        >
                                            {student.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {student.phone}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.source.source}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className="px-2 py-1 rounded-full text-xs"
                                                style={{ backgroundColor: student.stage.color + '20', color: student.stage.color }}
                                            >
                                                {student.stage.stage}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className="px-2 py-1 rounded-full text-xs"
                                                style={{ backgroundColor: student.priority.color + '20', color: student.priority.color }}
                                            >
                                                {student.priority.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {student.assignedTo?.name || 'Not Assigned'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {student.admissionStatus?.admissionStatus || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {student.collaborators && student.collaborators.length > 0 ? (
                                                <div className={`flex ${student.collaborators.length > 2 ? 'flex-col gap-1' : 'flex-row'}`}>
                                                    {student.collaborators.length > 2 ? (
                                                        student.collaborators.map((collaborator, index) => (
                                                            <span key={index}>{collaborator}</span>
                                                        ))
                                                    ) : (
                                                        <span>{student.collaborators.join(', ')}</span>
                                                    )}
                                                </div>
                                            ) : 'None'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {student.collaborateDate ? student.collaborateDate.toDate().toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/print-preview.svg`}
                                                    alt="preview-icon"
                                                    className="w-7 h-7 ml-2 cursor-pointer"
                                                    onClick={() => handleStudentClick(student.id)}
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/copy-phone.svg`}
                                                    alt="copy-number"
                                                    title='Copy Phone Number'
                                                    className="w-8 h-8 ml-2 cursor-pointer"
                                                    onClick={() => copyPhoneNumberToClipboard(student.phone)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Load More Button */}
                    {hasMore && (
                        <div className="mt-4 text-center">
                            <button
                                onClick={loadMore}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}

                    {showToast && (
                        <Toast
                            message={toastMessage}
                            isError={
                                toastMessage.includes('Error') ||
                                toastMessage.includes('Cannot') ||
                                toastMessage.includes('Failed')
                            }
                            show={showToast}
                            onClose={() => setShowToast(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Collaborations;